import { z } from "zod";

/** Create/update user via REST multipart `/admin/user` (camelCase fields). */
const UpdateUserSchema = z.object({
  userId: z.number().int().positive().optional(),
  authId: z.string().trim().min(1).optional(),
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.string().trim().min(1),
  isActive: z.boolean(),
});

export type UpdateUserDTOShape = z.infer<typeof UpdateUserSchema>;

/** REST `data` from `POST /admin/user` (camelCase). */
export interface IUpdateUserProps {
  userId: number;
  authId: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isBot: boolean;
  kind: string;
  isDeleted?: boolean;
  organizations?: { organizationId?: number; organizationName?: string }[];
}

export class UpdateUserDTO {
  readonly userId?: number;
  readonly authId?: string;
  readonly firstName!: string;
  readonly lastName!: string;
  readonly email!: string;
  readonly isActive!: boolean;

  constructor(data: UpdateUserDTOShape) {
    const validated = UpdateUserSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return UpdateUserSchema.safeParse(data);
  }

  /** Maps validated DTO → REST multipart body (camelCase). Auth ID is edit-only. */
  toMultipartFormData(): FormData {
    const formData = new FormData();
    formData.append("firstName", this.firstName.trim());
    formData.append("lastName", this.lastName.trim());
    formData.append("email", this.email.trim());
    formData.append("isActive", this.isActive ? "true" : "false");

    const isEdit = this.userId != null && this.userId > 0;
    if (isEdit) {
      formData.append("userId", String(this.userId));
      const authId = this.authId?.trim();
      if (authId) {
        formData.append("authId", authId);
      }
    } else {
      // Create: no authId. Empty userId passes API guard (`userId == null && !authId`).
      formData.append("userId", "");
    }

    return formData;
  }
}
